if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.syncVerticalThumbnailsHeight = this.syncVerticalThumbnailsHeight.bind(this);
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      if (this.dataset.desktopLayout === 'thumbnail_vertical') {
        if (window.ResizeObserver) {
          this.verticalThumbnailsResizeObserver = new ResizeObserver(this.syncVerticalThumbnailsHeight);
          // Observe the viewer only: on desktop just .is-active is displayed, so the
          // viewer's height tracks the active media (swap + image-load settle).
          this.verticalThumbnailsResizeObserver.observe(this.elements.viewer);
        }
        this.mql.addEventListener('change', this.syncVerticalThumbnailsHeight);
      this.setupThumbnailLooping();
    }
  }

  setupThumbnailLooping() {
    const slider = this.elements.thumbnails;
    if (!slider) return;
    slider.enableSliderLooping = true;

    const replaceWithWrap = (btn, isNext) => {
      if (!btn) return;
      const clone = btn.cloneNode(true);
      btn.parentNode.replaceChild(clone, btn);
      clone.removeAttribute('disabled');
      clone.addEventListener('click', (event) => {
        event.preventDefault();
        const axis = slider.sliderAxis;
        const el = slider.slider;
        if (!el) return;

        const step = parseInt(clone.dataset.step || 1);
        const itemOffset = slider.sliderItemOffset;
        const currentScroll = axis === 'vertical' ? el.scrollTop : el.scrollLeft;
        const maxScroll = axis === 'vertical' ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;

        if (isNext) {
          // If at or near the end, wrap to start.
          if (currentScroll >= maxScroll - 1.5) {
            if (axis === 'vertical') el.scrollTo({ top: 0 });
            else el.scrollTo({ left: 0 });
          } else {
            // ponytail: fallback unit ~64px when initPages hasn't set itemOffset yet
            const offset = itemOffset || (axis === 'vertical' ? 64 : 100);
            const target = currentScroll + step * offset;
            if (axis === 'vertical') el.scrollTo({ top: target });
            else el.scrollTo({ left: target });
          }
        } else {
          // If at or near the start, wrap to end.
          if (currentScroll <= 1.5) {
            if (axis === 'vertical') el.scrollTo({ top: maxScroll });
            else el.scrollTo({ left: maxScroll });
          } else {
            const offset = itemOffset || (axis === 'vertical' ? 64 : 100);
            const target = currentScroll - step * offset;
            if (axis === 'vertical') el.scrollTo({ top: target });
            else el.scrollTo({ left: target });
          }
        }
      });
    };

    replaceWithWrap(slider.querySelector('button[name="previous"]'), false);
    replaceWithWrap(slider.querySelector('button[name="next"]'), true);
  }

  disconnectedCallback() {
    if (this.verticalThumbnailsResizeObserver) {
      this.verticalThumbnailsResizeObserver.disconnect();
      this.verticalThumbnailsResizeObserver = null;
    }
    this.mql?.removeEventListener('change', this.syncVerticalThumbnailsHeight);
  }

  onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.syncVerticalThumbnailsHeight();
    }

    clearVerticalThumbnailsHeight() {
      if (!this.elements.thumbnails) return;
      this.elements.thumbnails.style.removeProperty('--thumbnail-vertical-height');
      this.elements.thumbnails.style.removeProperty('--thumbnail-vertical-list-height');
    }

    syncVerticalThumbnailsHeight() {
      if (!this.elements.thumbnails) return;
      if (this.dataset.desktopLayout !== 'thumbnail_vertical' || !this.mql.matches || !this.elements.viewer) {
        this.clearVerticalThumbnailsHeight();
        return;
      }

      const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
      if (!activeMedia) {
        this.clearVerticalThumbnailsHeight();
        return;
      }

      const activeHeight = Math.floor(activeMedia.getBoundingClientRect().height);
      const previousButton = this.elements.thumbnails.querySelector('button[name="previous"]');
      const nextButton = this.elements.thumbnails.querySelector('button[name="next"]');
      const previousButtonHeight = (previousButton && previousButton.offsetParent !== null) ? previousButton.offsetHeight : 0;
      const nextButtonHeight = (nextButton && nextButton.offsetParent !== null) ? nextButton.offsetHeight : 0;
      const listHeight = Math.max(0, activeHeight - previousButtonHeight - nextButtonHeight);

      if (activeHeight <= 0 || listHeight <= 0) {
        this.clearVerticalThumbnailsHeight();
        return;
      }

      this.elements.thumbnails.style.setProperty('--thumbnail-vertical-height', `${activeHeight}px`);
      this.elements.thumbnails.style.setProperty('--thumbnail-vertical-list-height', `${listHeight}px`);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.syncVerticalThumbnailsHeight();
      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      const isVertical = this.elements.thumbnails.dataset.sliderAxis === 'vertical';
      this.elements.thumbnails.slider.scrollTo(isVertical
        ? { top: thumbnail.offsetTop, behavior: 'smooth' }
        : { left: thumbnail.offsetLeft, behavior: 'smooth' }
      );
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
